/**
 * Validation utilities for different countries
 * This module provides validation functions for phone numbers and ID numbers
 * that can be easily extended to support multiple countries
 */

// ==================== ALGERIA (DZ) VALIDATIONS ====================

/**
 * Validate Algerian ID Number (18 digits)
 * @param {string|number} idNumber - The ID number to validate
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateDzIdNumber = (idNumber) => {
  if (!idNumber) {
    return {
      isValid: false,
      message: "ID number is required"
    };
  }

  const cleanId = String(idNumber).replace(/\s/g, '');

  if (cleanId.length !== 18) {
    return {
      isValid: false,
      message: "Algerian ID number must be exactly 18 digits"
    };
  }

  if (!/^\d{18}$/.test(cleanId)) {
    return {
      isValid: false,
      message: "ID number must contain only digits"
    };
  }

  return {
    isValid: true,
    message: "Valid Algerian ID number"
  };
};

/**
 * Validate Algerian Phone Number
 * Format: 0XXXXXXXXX (10 digits starting with 0) or +213XXXXXXXXX
 * Mobile: 05, 06, 07
 * Landline: 021-049
 * @param {string|number} phoneNumber - The phone number to validate
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateDzPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return {
      isValid: false,
      message: "Phone number is required"
    };
  }

  const cleanPhone = String(phoneNumber).replace(/[\s-]/g, '');

  // Check for international format +213XXXXXXXXX
  if (cleanPhone.startsWith('+213')) {
    const number = cleanPhone.substring(4);
    if (number.length !== 9) {
      return {
        isValid: false,
        message: "Algerian phone number must be 9 digits after +213"
      };
    }
    if (!/^\d{9}$/.test(number)) {
      return {
        isValid: false,
        message: "Phone number must contain only digits"
      };
    }
    return {
      isValid: true,
      message: "Valid Algerian phone number"
    };
  }

  // Check for national format 0XXXXXXXXX
  if (cleanPhone.startsWith('0')) {
    if (cleanPhone.length !== 10) {
      return {
        isValid: false,
        message: "Algerian phone number must be 10 digits starting with 0"
      };
    }
    if (!/^\d{10}$/.test(cleanPhone)) {
      return {
        isValid: false,
        message: "Phone number must contain only digits"
      };
    }
    // Validate second digit (mobile: 5,6,7 or landline: 2,3,4)
    const secondDigit = cleanPhone[1];
    if (!['2', '3', '4', '5', '6', '7'].includes(secondDigit)) {
      return {
        isValid: false,
        message: "Invalid Algerian phone number format"
      };
    }
    return {
      isValid: true,
      message: "Valid Algerian phone number"
    };
  }

  // Without prefix, should be 9 digits
  if (cleanPhone.length < 9) {
    return {
      isValid: false,
      message: "Phone number must be at least 9 digits"
    };
  }

  if (cleanPhone.length === 9 && /^\d{9}$/.test(cleanPhone)) {
    return {
      isValid: true,
      message: "Valid Algerian phone number"
    };
  }

  return {
    isValid: false,
    message: "Invalid phone number format"
  };
};

// ==================== GENERIC VALIDATION FUNCTIONS ====================

/**
 * Validate ID Number based on country code
 * @param {string|number} idNumber - The ID number to validate
 * @param {string} countryCode - ISO country code (e.g., 'DZ', 'FR', 'US')
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateIdNumber = (idNumber, countryCode = 'DZ') => {
  switch (countryCode.toUpperCase()) {
    case 'DZ':
      return validateDzIdNumber(idNumber);
    // Add more countries here
    // case 'FR':
    //   return validateFrIdNumber(idNumber);
    // case 'US':
    //   return validateUsIdNumber(idNumber);
    default:
      return {
        isValid: false,
        message: `ID validation for country ${countryCode} is not implemented yet`
      };
  }
};

/**
 * Validate Phone Number based on country code
 * @param {string|number} phoneNumber - The phone number to validate
 * @param {string} countryCode - ISO country code (e.g., 'DZ', 'FR', 'US')
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validatePhoneNumber = (phoneNumber, countryCode = 'DZ') => {
  switch (countryCode.toUpperCase()) {
    case 'DZ':
      return validateDzPhoneNumber(phoneNumber);
    // Add more countries here
    // case 'FR':
    //   return validateFrPhoneNumber(phoneNumber);
    // case 'US':
    //   return validateUsPhoneNumber(phoneNumber);
    default:
      return {
        isValid: false,
        message: `Phone validation for country ${countryCode} is not implemented yet`
      };
  }
};

// ==================== PLACEHOLDER FOR FUTURE COUNTRIES ====================

// Example structure for adding new countries:
/*
export const validateFrIdNumber = (idNumber) => {
  // French ID validation logic
  return { isValid: boolean, message: string };
};

export const validateFrPhoneNumber = (phoneNumber) => {
  // French phone validation logic
  return { isValid: boolean, message: string };
};
*/

// Export all validators
export default {
  // Algeria
  validateDzIdNumber,
  validateDzPhoneNumber,
  
  // Generic
  validateIdNumber,
  validatePhoneNumber
};
